import {
  Scene,
  SceneAssetItem,
  SceneAudioItem,
  SceneItem,
  SceneItemType,
  SceneVoiceoverItem,
} from '@monorepo/types';
import React, { FunctionComponent } from 'react';
import SceneAsset from './items/scene-asset/scene-asset';
import SceneVoiceover from './items/scene-voiceover/scene-voiceover';
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import { Button, Card, Flex, Typography, Tooltip } from 'antd';
import { useVideoContext } from '../../../../hooks/use-video';
import { VideoPart } from '../../video-editor-types';
import SceneItemTitle from './scene-item-title';
import { useConfirm } from '../../../../hooks';
import { useTranslation } from 'react-i18next';

import './scene-item.scss';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import SceneItemPosition from './scene-item-position';
import SceneAudio from './items/scene-audio/scene-audio';

dayjs.extend(duration);

export const formatTime = (seconds: number): string => {
  // Calculate minutes and seconds
  const minutes = Math.floor(seconds / 60);
  const wholeSeconds = Math.floor(seconds % 60);

  // Format minutes and seconds with leading zeros
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(wholeSeconds).padStart(2, '0');

  // Get milliseconds and always format to 2 decimal places
  const decimal = seconds % 1;
  const millisStr = decimal.toFixed(2).substring(1);

  return `${formattedMinutes}:${formattedSeconds}${millisStr}`;
};

export const formatSceneTime = (scene: SceneItem): string => {
  const startFormatted = formatTime(scene.start);
  const endFormatted = formatTime(scene.end);
  return `${startFormatted}-${endFormatted}`;
};

/* Example outputs:
console.log(formatTime(0));     // "00:00.00"
console.log(formatTime(6.78));  // "00:06.78"
console.log(formatTime(10.1));  // "00:10.10"
*/

interface Props {
  item: SceneItem;
  scene: Scene;
}

const SceneItemComponent: FunctionComponent<Props> = ({ item, scene }) => {
  const { t } = useTranslation();
  const {
    setSelectedSceneItemIndex,
    setSceneId,
    deleteSceneItem,
    setCurrentVideoPart,
  } = useVideoContext();
  const { confirm } = useConfirm();

  const getItemByType = () => {
    if (item.type === SceneItemType.Asset) {
      return (
        <SceneAsset
          index={item.index}
          scene={scene}
          data={item.data as SceneAssetItem}
        />
      );
    }
    if (item.type === SceneItemType.Voiceover) {
      return (
        <SceneVoiceover scene={scene} data={item.data as SceneVoiceoverItem} />
      );
    }
    if (item.type === SceneItemType.Audio) {
      return (
        <SceneAudio
          itemIndex={item.index}
          scene={scene}
          data={item.data as SceneAudioItem}
        />
      );
    }

    return null;
  };

  const selectSceneItem = () => {
    setSelectedSceneItemIndex(item.index);
    setSceneId(scene._id);
    setCurrentVideoPart(VideoPart.EditSceneItem);
  };

  const showDeleteConfirm = () => {
    confirm({
      title: t('videoEditor.sceneItem.deleteConfirm'),
      icon: <ExclamationCircleFilled />,
      okText: t('videoEditor.sceneItem.deleteOk'),
      okType: 'danger',
      onOk: async () => {
        await deleteSceneItem({
          videoId: scene.videoId,
          sceneId: scene._id,
          itemIndex: item.index,
        });

        console.log('Deleted successfully');
      },
    });
  };

  return (
    <Card hoverable={true} className="scene-item">
      <Flex
        align={'center'}
        className={'scene-item-header'}
        justify={'space-between'}
      >
        <SceneItemTitle item={item} />
        <Flex className={'scene-item-actions'} gap={0}>
          <SceneItemPosition scene={scene} sceneItem={item} />
          <Tooltip title={t('common.edit')}>
            <Button
              onClick={selectSceneItem}
              size={'middle'}
              type={'text'}
              icon={<EditOutlined />}
            />
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <Button
              onClick={showDeleteConfirm}
              size={'middle'}
              type={'text'}
              icon={<DeleteOutlined />}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <div className="scene-items-list">{getItemByType()}</div>
      <Flex justify={'end'}>
        <Typography.Text type={'secondary'} style={{ fontSize: '12px' }}>
          {formatSceneTime(item)}
        </Typography.Text>
      </Flex>
    </Card>
  );
};

export default SceneItemComponent;
